import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Home, ChevronLeft, ChevronRightIcon, Loader2, Wallet, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getSocioPagosConSos } from '../services/cuotasService.tsx';
import { useAuth0 } from '@auth0/auth0-react';

type MovimientoSosItem = {
  idcomprobante?: string | number;
  fecha?: string;
  clipro?: string;
  memo?: string;
  fcncnd?: string;
  letra?: string;
  sucursal?: number;
  numero?: number;
  montodebe?: number;
  montohaber?: number;
  montosaldo?: number;
  idtipo_operacion?: number;
  estadoasociacion?: number;
};

const ITEMS_PER_PAGE = 13;

const esRecibo = (m: MovimientoSosItem) => Number(m.montohaber || 0) > 0;

const facturaCompensada = (m: MovimientoSosItem) =>
  !esRecibo(m) && Number(m.estadoasociacion || 0) === 3;

const saldoPendienteMovimiento = (m: MovimientoSosItem) => {
  if (esRecibo(m) || facturaCompensada(m)) return 0;
  return Math.max(0, Number(m.montosaldo ?? m.montodebe ?? 0));
};

const movimientoPendiente = (m: MovimientoSosItem) =>
  !esRecibo(m) && saldoPendienteMovimiento(m) > 0;

export default function CuotasTable() {
  const [movimientosCuotas, setMovimientosCuotas] = useState<MovimientoSosItem[]>([]);
  const [payingId, setPayingId] = useState<string | number | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState<'todas' | 'pendientes' | 'vencidas'>('todas');
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const inferTipoMovimiento = (m: MovimientoSosItem): 'cuota' | 'servicio' | 'caja' => {
    const text = `${m.memo || ''} ${m.fcncnd || ''} ${m.letra || ''}`.toLowerCase();
    if (text.includes('cuota') || text.includes('social')) return 'cuota';
    if (text.includes('caja') || text.includes('cofre')) return 'caja';
    return 'servicio';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const storedSocio = localStorage.getItem('socioSeleccionado');
        if (!storedSocio) throw new Error('No hay socio seleccionado');

        const socio = JSON.parse(storedSocio);
        const id = socio.socio_id;
        if (!id) throw new Error('No se pudo identificar el socio seleccionado');

        setLoading(true);

        const detalle = await getSocioPagosConSos(id, getAccessTokenSilently, {
          includeSosMovimientos: true,
        });

        const movimientos = Array.isArray(detalle?.movimientos_sos) ? detalle.movimientos_sos : [];
        const soloCuotas = movimientos.filter((m: MovimientoSosItem) => inferTipoMovimiento(m) === 'cuota');

        setMovimientosCuotas(soloCuotas);
        setLastSyncAt(detalle?.movimientos_sos_info?.last_sync_at || null);
        setError(null);
      } catch (err: any) {
        setError(err?.message || 'Error al obtener cuotas');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently]);

  const cuotasFiltradas = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    return movimientosCuotas.filter((m) => {
      const pendiente = movimientoPendiente(m);
      const fecha = m.fecha ? new Date(m.fecha) : null;
      if (fecha) fecha.setHours(0, 0, 0, 0);
      const vencida = pendiente && !!fecha && fecha < hoy;

      if (filtroEstado === 'pendientes') return pendiente;
      if (filtroEstado === 'vencidas') return vencida;
      return true;
    });
  }, [movimientosCuotas, filtroEstado]);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(cuotasFiltradas.length / ITEMS_PER_PAGE));
  const currentRows = cuotasFiltradas.slice(startIndex, endIndex);

  const syncLabel = useMemo(() => {
    if (!lastSyncAt) return 'Sin sincronización reciente';
    const last = new Date(lastSyncAt).getTime();
    if (Number.isNaN(last)) return 'Sincronización reciente';
    const diffMin = Math.max(0, Math.floor((Date.now() - last) / 60000));
    if (diffMin < 1) return 'Actualizado hace instantes';
    if (diffMin < 60) return `Actualizado hace ${diffMin} min`;
    const hours = Math.floor(diffMin / 60);
    return `Actualizado hace ${hours} h`;
  }, [lastSyncAt]);

  const resumen = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const pendientes = movimientosCuotas.filter((m) => movimientoPendiente(m));
    const vencidas = pendientes.filter((m) => {
      if (!m.fecha) return false;
      const f = new Date(m.fecha);
      f.setHours(0, 0, 0, 0);
      return f < hoy;
    });

    const totalPendiente = pendientes.reduce((acc, m) => acc + saldoPendienteMovimiento(m), 0);

    return {
      total: movimientosCuotas.length,
      pendientes: pendientes.length,
      vencidas: vencidas.length,
      totalPendiente,
    };
  }, [movimientosCuotas]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filtroEstado]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const pagarCuota = async (m: MovimientoSosItem, idx: number) => {
    try {
      const pagoId = m.idcomprobante || `${idx}`;
      setPayingId(String(pagoId));
      setPayError(null);

      const token = await getAccessTokenSilently().catch(() => null);
      const apiBase = (process.env.REACT_APP_BACK_URL || '').replace(/\/$/, '');
      const baseUrl = window.location.origin;

      const resp = await fetch(`${apiBase}/mercadopago/preferencias`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          pagoId,
          tipo: 'cuota',
          auto_return: 'approved',
          back_urls: {
            success: `${baseUrl}/mercadopago/success`,
            failure: `${baseUrl}/mercadopago/failure`,
            pending: `${baseUrl}/mercadopago/failure`,
          },
          notification_url: `${apiBase}/mercadopago/webhook`,
        }),
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'No se pudo generar la preferencia de pago');
      }

      const pref = await resp.json();
      const redirect = pref.init_point || pref.sandbox_init_point;
      if (!redirect) throw new Error('No se recibió init_point');
      window.location.href = redirect;
    } catch (err: any) {
      setPayError(err?.message || 'No se pudo iniciar el pago.');
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {payingId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <Loader2 className="h-7 w-7 animate-spin" />
            </div>
            <h3 className="mt-4 text-xl font-semibold text-slate-900">Preparando el pago</h3>
          </div>
        </div>
      )}
      <div className="container mx-auto px-4 py-8">
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <button onClick={() => navigate('/inicio')} className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </button>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Estado de Cuotas</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1 text-left">Estado de Cuotas</h1>
            <p className="text-sm text-gray-600">Vista idéntica a Pagos, filtrada por cuotas.</p>
            <p className="text-xs text-slate-500 mt-1">{syncLabel}</p>
          </div>
          <button
            onClick={() => navigate('/pagos')}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            <Wallet className="h-4 w-4" />
            Ir a pagos
          </button>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <button
            onClick={() => setFiltroEstado('todas')}
            className={`rounded-xl border bg-white p-4 text-center transition ${filtroEstado === 'todas' ? 'border-blue-300 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <p className="text-xs text-slate-500">Cuotas registradas</p>
            <p className="text-2xl font-bold text-slate-900">{resumen.total}</p>
          </button>
          <button
            onClick={() => setFiltroEstado('pendientes')}
            className={`rounded-xl border bg-amber-50 p-4 text-center transition ${filtroEstado === 'pendientes' ? 'border-amber-400 ring-2 ring-amber-100' : 'border-amber-200 hover:border-amber-300'}`}
          >
            <p className="text-xs text-amber-700">Pendientes</p>
            <p className="text-2xl font-bold text-amber-800">{resumen.pendientes}</p>
          </button>
          <button
            onClick={() => setFiltroEstado('vencidas')}
            className={`rounded-xl border p-4 text-center transition ${resumen.vencidas > 0 ? 'bg-rose-50' : 'bg-emerald-50'} ${filtroEstado === 'vencidas' ? (resumen.vencidas > 0 ? 'border-rose-400 ring-2 ring-rose-100' : 'border-emerald-400 ring-2 ring-emerald-100') : (resumen.vencidas > 0 ? 'border-rose-300 hover:border-rose-400' : 'border-emerald-200 hover:border-emerald-300')}`}
          >
            <p className={`text-xs ${resumen.vencidas > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>Vencidas</p>
            <div className="flex items-center justify-center gap-2">
              {resumen.vencidas > 0 && <AlertTriangle className="h-4 w-4 text-rose-600" />}
              <p className={`text-2xl font-bold ${resumen.vencidas > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{resumen.vencidas}</p>
            </div>
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <div>
                <p className="text-sm font-semibold text-rose-800">{error}</p>
                {String(error).toLowerCase().includes('no hay socio') && (
                  <p className="text-xs text-rose-600">
                    Seleccioná un socio para poder ver sus cuotas y pagos.
                  </p>
                )}
              </div>
            </div>
            {String(error).toLowerCase().includes('no hay socio') && (
              <button
                onClick={() => navigate('/seleccionar-socio')}
                className="shrink-0 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-rose-700"
              >
                Seleccionar socio
              </button>
            )}
          </div>
        )}
        {payError && <p className="text-red-600 mb-4">{payError}</p>}
        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            Obteniendo datos de tus cuotas... un segundito.
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
            <p>
              Mostrando {cuotasFiltradas.length ? `${startIndex + 1}-${Math.min(endIndex, cuotasFiltradas.length)}` : 0} de {cuotasFiltradas.length} cuotas ({filtroEstado})
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Fecha</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Documento</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Comprobante</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Detalle</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Movimiento</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">Importe</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Estado</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading
                  ? Array.from({ length: 8 }).map((_, idx) => (
                      <tr key={`skeleton-${idx}`} className="animate-pulse">
                        <td className="px-3 py-3"><div className="h-3 w-20 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-5 w-20 rounded-full bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-3 w-24 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-3 w-40 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-3 w-24 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="ml-auto h-3 w-16 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-5 w-20 rounded-full bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="ml-auto h-8 w-16 rounded bg-slate-200" /></td>
                      </tr>
                    ))
                  : currentRows.map((m, idx) => {
                      const comp = [m.fcncnd, m.letra, m.sucursal, m.numero]
                        .filter((x) => x !== null && x !== undefined && String(x) !== '')
                        .join('-');
                      return (
                        <tr key={`${m.idcomprobante || 'mov'}-${idx}`}>
                          <td className="px-3 py-2 text-slate-700">
                            {m.fecha ? new Date(m.fecha).toLocaleDateString('es-AR') : '—'}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${Number(m.montohaber || 0) > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}`}>
                              {Number(m.montohaber || 0) > 0 ? 'Recibo' : 'Factura'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{comp || m.idcomprobante || '—'}</td>
                          <td className="px-3 py-2 text-slate-600">{m.memo || `Tipo op. ${m.idtipo_operacion ?? '—'}`}</td>
                          <td className="px-3 py-2 text-slate-700">
                            {Number(m.montohaber || 0) > 0 ? 'Pago recibido' : facturaCompensada(m) ? 'Cargo compensado' : 'Cargo generado'}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-900">
                            ${Number((m.montohaber || 0) > 0 ? m.montohaber : m.montodebe || 0).toLocaleString('es-AR')}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {(() => {
                              const pendiente = movimientoPendiente(m);
                              const recibo = esRecibo(m);
                              return (
                                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${recibo ? 'bg-emerald-100 text-emerald-700' : pendiente ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                  {recibo ? 'Pago recibido' : pendiente ? 'Pendiente' : 'Compensado'}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {movimientoPendiente(m) ? (
                              <button
                                onClick={() => pagarCuota(m, idx)}
                                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                              >
                                Pagar
                              </button>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6 mt-3">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
            </button>
            <span className="text-sm text-gray-700">Página {currentPage} de {totalPages}</span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Siguiente <ChevronRightIcon className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
